/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-02-03 17:18:33
 **/

import { Environments } from "@rsupport/libraries";

export default class SoundMeter {
	private context: AudioContext;
	private name: string;
	public instant: number;
	public slow: number;
	public clip: number;
	private source: MediaStreamAudioSourceNode | null;
	private processorNode: AudioWorkletNode | null;

	constructor(name: string, audioContext: AudioContext) {
		this.name = name; // SoundMeter 이름
		this.context = audioContext; // AudioContext 참조
		this.instant = 0.0; // 즉각적인 볼륨
		this.slow = 0.0; // 느린 볼륨
		this.clip = 0.0; // 클리핑 비율
		this.source = null; // 오디오 소스
		this.processorNode = null; // AudioWorkletNode
	}

	// 비동기 AudioWorklet 초기화 함수
	private async _initAudioWorklet(): Promise<void> {
		try {
			if (!this.context.audioWorklet) {
				throw new Error("AudioContext does not support AudioWorklet.");
			}

			// AudioWorkletProcessor 등록
			await this.context.audioWorklet.addModule(
				`${location.host.startsWith("localhost") && Environments.env("UNITY_BUILD", false) ? "/static" : ""}/sound-meter-processor.js?t=${Date.now()}`
			);
			console.debug("#SoundMeter# AudioWorklet module loaded.");
		} catch (error) {
			console.error("#SoundMeter# Failed to load AudioWorklet module:", error);
		}
	}

	// 오디오 소스를 AudioWorkletNode에 연결
	public async connectToSource(stream: MediaStream): Promise<void> {
		// eslint-disable-next-line no-useless-catch
		try {
			await this._initAudioWorklet();

			console.debug("#SoundMeter# Connecting to audio source...");
			const audioTracks = stream.getAudioTracks();

			if (audioTracks.length === 0) {
				console.debug("#SoundMeter# No audio tracks found.");
				return;
			}

			// MediaStreamSource 생성
			this.source = this.context.createMediaStreamSource(stream);

			// AudioWorkletNode 생성
			this.processorNode = new AudioWorkletNode(this.context, "sound-meter-processor");

			// AudioWorkletNode로부터 데이터 수신
			this.processorNode.port.onmessage = (event: MessageEvent) => {
				const { instant, slow, clip } = event.data as SoundMeterData;
				this.instant = instant;
				this.slow = slow;
				this.clip = clip;
			};

			// 노드 연결
			this.source.connect(this.processorNode);
			this.processorNode.connect(this.context.destination);
		} catch (error) {
			throw error;
		}
	}

	// 오디오 소스 연결 해제
	public stop(): void {
		console.debug("#SoundMeter# Stopping...");
		if (this.source) {
			this.source.disconnect();
			this.source = null;
		}
		if (this.processorNode) {
			this.processorNode.disconnect();
			this.processorNode = null;
		}
		console.debug("#SoundMeter# Stopped.");
	}
}

// AudioWorklet으로부터의 데이터 포맷
interface SoundMeterData {
	instant: number;
	slow: number;
	clip: number;
}
